/**
 * BUG-platform — Reusable column-sort hook.
 *
 * Usage:
 *   const sort = useSort();
 *   const sorted = sort.apply(rows, {
 *     name:     (r) => r.name?.toLowerCase(),
 *     created:  (r) => new Date(r.created_at).getTime(),
 *   });
 *   ...
 *   <SortableHeader sort={sort} k="name">{t('common.name')}</SortableHeader>
 *
 * Click cycle: none → asc → desc → none.
 */
import { useCallback, useMemo, useState } from 'react';

export function useSort(initialKey = null, initialDir = 'asc') {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);

  const toggle = useCallback((key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null);
    setSortDir('asc');
  }, [sortKey, sortDir]);

  const apply = useCallback((rows, getters) => {
    if (!sortKey || !getters?.[sortKey]) return rows;
    const get = getters[sortKey];
    const out = [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  }, [sortKey, sortDir]);

  return useMemo(() => ({
    sortKey, sortDir, toggle, apply,
    is: (k) => sortKey === k,
    dirOf: (k) => (sortKey === k ? sortDir : null),
  }), [sortKey, sortDir, toggle, apply]);
}
